import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authSelectors } from 'redux/auth';
import { workspaceActions, workspaceSelectors, workspaceTypes } from 'redux/workspace';
import useReduxLoading from 'hooks/useReduxLoading';
import { isMobile } from 'helpers';

const greeting = { type: 'greeting', config: { showAvatar: isMobile } };

// Without a layout only the greeting is known to belong on Home
const failedWidgets = [greeting];

// A signed-in account with no workspace in this app has no layout to load
const noWorkspaceWidgets = [greeting, { type: 'no_workspace' }];

// The backend puts the workspace's custom greeting in the greeting's config; without a layout it comes from auth/user
const withCustomGreeting = (widgets, customGreeting) => (customGreeting
  ? widgets.map(widget => (widget.type === 'greeting' ? { ...widget, config: { ...widget.config, subtitle: customGreeting } } : widget))
  : widgets);

// The app download prompt is the client's own: it shows itself only where this device registered or joined
export const withAppDownload = (widgets, mobile) => {
  if (mobile) {
    return widgets;
  }

  const at = widgets.findIndex(({ type }) => type === 'greeting') + 1;

  return [...widgets.slice(0, at), { id: 'app_download', type: 'app_download' }, ...widgets.slice(at)];
};

const useHomeLayout = () => {

  const dispatch = useDispatch();
  const workspaceId = useSelector(authSelectors.getWorkspaceId);

  const getHomeWidgets = useSelector(workspaceSelectors.getHomeWidgetsSelector);
  const widgets = getHomeWidgets(workspaceId);

  // The layout follows the member's permissions and features, and the greeting widget the workspace's custom greeting,
  // so a change to either loads it again
  const memberKey = useSelector(authSelectors.getMemberKey);
  const customGreeting = useSelector(authSelectors.getWorkspace)?.customGreeting;
  const layoutKey = JSON.stringify([memberKey, customGreeting ?? null]);
  const loadedForLayoutKey = useSelector(workspaceSelectors.getHomeLayoutKey);

  const load = useCallback(() => {
    dispatch(workspaceActions.getHome({ workspaceId, layoutKey }));
  }, [dispatch, workspaceId, layoutKey]);

  useEffect(() => {
    if (workspaceId && (!widgets || loadedForLayoutKey !== layoutKey)) {
      load();
    }
  }, [load, widgets, workspaceId, layoutKey, loadedForLayoutKey]);

  const requesting = useReduxLoading(
    [workspaceTypes.getHome],
    [workspaceTypes.getHomeSuccess, workspaceTypes.getHomeFailure],
    true,
    workspaceId,
  );

  return useMemo(() => {
    if (!workspaceId) {
      return { widgets: noWorkspaceWidgets, loading: false };
    }

    return {
      widgets: withAppDownload(widgets ?? withCustomGreeting(failedWidgets, customGreeting), isMobile),
      loading: !widgets && requesting,
      failed: !widgets && !requesting,
      retry: load,
    };
  }, [workspaceId, widgets, requesting, customGreeting, load]);
};

export default useHomeLayout;
